import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Badge, Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment, Stack,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import StarIcon from "@mui/icons-material/Star";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, EmptyState } from "../ui";
import { useAuth } from "../../context/AuthContext";
import MessageConversationView from "./MessageConversationView";

/* ---------- helpers ---------- */
function timeAgo(date) {
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
function fmtFull(d) {
  return new Date(d).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}
function initials(s) {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/);
  return (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
}
function senderName(m) {
  return m.senderName || m.sender?.name || (typeof m.sender === "string" ? m.sender : null) || "Unknown";
}
function senderEmail(m) {
  return m.senderEmail || m.sender?.email || "";
}
function senderId(m) {
  const id = m.senderId || m.sender?._id || m.sender?.id;
  return id ? String(id) : "";
}
function avatarColor(name) {
  const colors = ["#6C50BF", "#39C645", "#F9BC06", "#5C85FF", "#EC4899", "#F97316", "#14B8A6"];
  let sum = 0;
  for (const c of String(name || "")) sum += c.charCodeAt(0);
  return colors[sum % colors.length];
}
function fileIcon(url = "") {
  const ext = url.split(".").pop().toLowerCase();
  if (ext === "pdf") return <PictureAsPdfOutlinedIcon sx={{ color: "#EF4444" }} />;
  return <InsertDriveFileOutlinedIcon sx={{ color: "primary.main" }} />;
}

/* ---------- main ---------- */
export default function MyMessagesNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unread | starred
  const [search, setSearch] = useState("");
  const [starred, setStarred] = useState(() => {
    try { return JSON.parse(localStorage.getItem("msg:starred") || "[]"); } catch { return []; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/mailmessage/getallloginedConversation");
      const list = Array.isArray(res.data?.messages) ? res.data.messages : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setMessages(list);
      if (list.length && !activeId) setActiveId(list[0]._id);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load inbox");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    localStorage.setItem("msg:starred", JSON.stringify(starred));
  }, [starred]);

  const toggleStar = (id) => {
    setStarred((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/api/mailmessage/updateReadStatus/${id}`);
      setMessages((ms) => ms.map((m) => m._id === id ? { ...m, read: true } : m));
    } catch {}
  };

  const onPick = (m) => {
    setActiveId(m._id);
    if (!m.read) markRead(m._id);
  };

  const filtered = useMemo(() => {
    let out = messages;
    if (filter === "unread") out = out.filter((m) => !m.read);
    else if (filter === "starred") out = out.filter((m) => starred.includes(m._id));
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((m) =>
        (m.subject || "").toLowerCase().includes(q) ||
        (m.message || "").toLowerCase().includes(q) ||
        senderName(m).toLowerCase().includes(q)
      );
    }
    return out;
  }, [messages, filter, search, starred]);

  const unreadCount = messages.filter((m) => !m.read).length;
  const active = messages.find((m) => m._id === activeId);

  return (
    <Box p={3}>
      <PageHeader
        title="Inbox"
        subtitle={unreadCount > 0 ? `${unreadCount} unread of ${messages.length} total` : `${messages.length} messages`}
        icon={<InboxOutlinedIcon />}
        actions={[
          <Button key="sent" variant="outlined" startIcon={<SendOutlinedIcon />} onClick={() => nav("/MySentMessages")}>
            Sent
          </Button>,
          <Button key="compose" variant="contained" startIcon={<MailOutlineOutlinedIcon />} onClick={() => nav("/SendMessage")}>
            Compose
          </Button>,
        ]}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "380px 1fr" }, gap: 2, minHeight: 600 }}>
        {/* ============== LIST PANE ============== */}
        <SectionCard sx={{ p: 0, overflow: "hidden", display: { xs: active ? "none" : "block", md: "block" } }}>
          <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <TextField
              fullWidth size="small" placeholder="Search messages…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Stack direction="row" spacing={0.5} mt={1.5}>
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")} count={messages.length}>All</FilterChip>
              <FilterChip active={filter === "unread"} onClick={() => setFilter("unread")} count={unreadCount} highlight>Unread</FilterChip>
              <FilterChip active={filter === "starred"} onClick={() => setFilter("starred")} count={starred.length}>Starred</FilterChip>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ maxHeight: { md: "calc(100vh - 250px)" }, overflowY: "auto" }}>
            {loading && messages.length === 0 ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress size={24} /></Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                <InboxOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                <Typography sx={{ fontSize: 13 }}>
                  {search ? "No messages match your search." : filter === "unread" ? "No unread messages — inbox zero!" : filter === "starred" ? "No starred messages yet." : "Your inbox is empty."}
                </Typography>
              </Box>
            ) : (
              filtered.map((m) => (
                <MessageRow
                  key={m._id} message={m}
                  active={activeId === m._id}
                  starred={starred.includes(m._id)}
                  onClick={() => onPick(m)}
                  onToggleStar={(e) => { e.stopPropagation(); toggleStar(m._id); }}
                />
              ))
            )}
          </Box>
        </SectionCard>

        {/* ============== PREVIEW PANE ============== */}
        <SectionCard sx={{ p: 0, overflow: "hidden", display: { xs: active ? "block" : "none", md: "block" } }}>
          {!active ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 400, color: "text.secondary", p: 4 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#F4EDFA", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                <MailOutlineOutlinedIcon sx={{ fontSize: 36, color: "primary.main" }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>Select a message</Typography>
              <Typography sx={{ fontSize: 13, textAlign: "center", maxWidth: 320 }}>
                Pick any message from the list to read it here. Unread messages are marked automatically.
              </Typography>
            </Box>
          ) : (
            <MessageDetail
              message={active}
              myUserId={user?._id != null ? String(user._id) : user?.id != null ? String(user.id) : ""}
              starred={starred.includes(active._id)}
              onBack={() => setActiveId(null)}
              onToggleStar={() => toggleStar(active._id)}
              onSent={load}
            />
          )}
        </SectionCard>
      </Box>
    </Box>
  );
}

/* ---------- sub-components ---------- */

function FilterChip({ active, onClick, children, count, highlight }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.25, py: 0.5, borderRadius: 6, cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 0.75,
        fontSize: 12, fontWeight: 700,
        bgcolor: active ? "primary.main" : "transparent",
        color: active ? "#fff" : highlight && count > 0 ? "primary.main" : "text.secondary",
        border: "1px solid", borderColor: active ? "primary.main" : "divider",
        transition: "all 0.15s",
        "&:hover": { bgcolor: active ? "primary.dark" : "action.hover" },
      }}
    >
      {children}
      {count !== undefined && (
        <Box sx={{ fontSize: 10, fontWeight: 800, px: 0.6, py: 0.1, borderRadius: 5,
          bgcolor: active ? "rgba(255,255,255,0.25)" : highlight && count > 0 ? "#F4EDFA" : "action.hover",
          color: active ? "#fff" : highlight && count > 0 ? "primary.main" : "text.secondary",
        }}>
          {count}
        </Box>
      )}
    </Box>
  );
}

function MessageRow({ message, active, starred, onClick, onToggleStar }) {
  const name = senderName(message);
  const color = avatarColor(name);
  const hasFiles = (message.files?.length || 0) > 0;
  const preview = String(message.message || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        p: 1.5,
        cursor: "pointer",
        borderLeft: "3px solid",
        borderLeftColor: active ? "primary.main" : !message.read ? "primary.light" : "transparent",
        bgcolor: active ? "#F4EDFA" : !message.read ? "#FAFAFC" : "transparent",
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        transition: "all 0.12s",
        "&:hover": { bgcolor: active ? "#F4EDFA" : "action.hover" },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ width: 36, height: 36, bgcolor: color, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {initials(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 13, fontWeight: !message.read ? 800 : 600, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", whiteSpace: "nowrap", ml: 1, flexShrink: 0 }}>
              {timeAgo(message.createdAt)}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, fontWeight: !message.read ? 700 : 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.25 }}>
            {message.subject || "(no subject)"}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
            <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
              {preview || "(no body)"}
            </Typography>
            {hasFiles && <AttachFileOutlinedIcon sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }} />}
            <IconButton size="small" onClick={onToggleStar} sx={{ p: 0.25, color: starred ? "#F9BC06" : "text.disabled" }}>
              {starred ? <StarIcon sx={{ fontSize: 16 }} /> : <StarOutlineIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Stack>
        </Box>
      </Stack>
      {!message.read && (
        <Box sx={{ position: "absolute", top: 14, right: 14, width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
      )}
    </Box>
  );
}

function MessageDetail({ message, myUserId, starred, onBack, onToggleStar, onSent }) {
  const otherName = senderName(message);
  const otherEmail = senderEmail(message);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton onClick={onBack} sx={{ display: { md: "none" } }}><ArrowBackIcon /></IconButton>
        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1, lineHeight: 1.35 }}>
          {message.subject || "(no subject)"}
        </Typography>
        <Tooltip title={starred ? "Unstar" : "Star"}>
          <IconButton onClick={onToggleStar} sx={{ color: starred ? "#F9BC06" : "text.secondary" }}>
            {starred ? <StarIcon /> : <StarOutlineIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <MessageConversationView
        conversationId={message.conversationId ? String(message.conversationId) : ""}
        myUserId={myUserId}
        otherName={otherName}
        otherEmail={otherEmail}
        subject={message.subject}
        seedMessage={message}
        onSent={onSent}
      />
    </Box>
  );
}
